import React, { useEffect, useMemo, useState } from 'react';
import { money } from '../../../utils/money';
import { Btn, Chip, DataTable, Loading, Msg, Section, Select, TextInput, fmtDate, useAdminGet } from '../kit';
import OrderDrawer from './OrderDrawer';
import {
  EMPTY_TAG_FILTER, LIFECYCLE_LABEL, TagFilter, TagFilterValue, UserTagCells, describeTagFilter, tagFilterActive,
  tagFilterParams, tagText, toLifecycle, useTagOptions,
} from '../tags/shared';
import {
  Pager, S, STATUSES, STATUS_META, StatusPill, ExportButtons, dueAt, isOverdue, num, pageInfo, rowsOf,
  str, useDebounced,
} from './shared';

/**
 * Orders — every IP Intel order, paged and filtered on the server
 * (GET ip_intel/orders), with the drawer for one order on a row click.
 * Two pseudo-statuses sit in the status filter next to the real ones:
 * "open" (paid and not yet delivered or cancelled) and "overdue" (intake in,
 * past the turnaround promise, not delivered).
 */

const OPEN = 'open';
const OVERDUE = 'overdue';

const SORT_BY_COL: Record<string, string> = {
  ref: 'ref', created_at: 'created_at', paid_at: 'paid_at', total_minor: 'total',
  intake_submitted_at: 'intake_submitted_at', due: 'due_at', delivered_at: 'delivered_at', status: 'status',
};

const COLS = ['ref', 'status', 'customer', 'lifecycle_tags', 'organisation', 'technology_title', 'total_minor', 'due', 'assigned_to', 'created_at',
  'paid_at', 'intake_submitted_at', 'delivered_at', 'region_code'];
const LABELS: Record<string, string> = {
  ref: 'Ref', status: 'Status', customer: 'Customer', lifecycle_tags: 'Lifecycle · tags', organisation: 'Organisation', technology_title: 'Technology',
  total_minor: 'Total',
  region_code: 'Region', created_at: 'Created', paid_at: 'Paid', intake_submitted_at: 'Intake in',
  due: 'Due', delivered_at: 'Delivered', assigned_to: 'Assignee',
};

/** The account's lifecycle + tags as text (user_tags.decorate) — what the
 *  table's own search, column filter and CSV see; the cell renders pills. */
const lifecycleText = (r: any) => {
  const lc = toLifecycle(r?.lifecycle);
  return [r?.lifecycle_label || LIFECYCLE_LABEL[lc] || lc, tagText(r)].filter(Boolean).join(' · ');
};

type Filters = { q: string; status: string; from: string; to: string };
const EMPTY: Filters = { q: '', status: '', from: '', to: '' };

const Orders: React.FC<{
  preset?: { status: string; nonce: number } | null;
  turnaroundDays?: number;
  onChanged?: () => void;
}> = ({ preset, turnaroundDays = 7, onChanged }) => {
  const [f, setF] = useState<Filters>({ ...EMPTY, status: preset?.status || '' });
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState('50');
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>({ key: 'created_at', dir: 'desc' });
  const [openId, setOpenId] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [dlErr, setDlErr] = useState('');
  const q = useDebounced(f.q);
  // The customer account's lifecycle + tags (standard params; server-side,
  // and the exports honour them too). A guest order never matches one.
  const [tagF, setTagF] = useState<TagFilterValue>(EMPTY_TAG_FILTER);
  const tagOpts = useTagOptions();

  // Overview's KPI tiles hand a status over; a new nonce re-applies it.
  useEffect(() => {
    if (preset) { setF({ ...EMPTY, status: preset.status }); setTagF(EMPTY_TAG_FILTER); }
  }, [preset?.nonce]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k: keyof Filters) => (v: string) => setF(s => ({ ...s, [k]: v }));

  const filterParams = useMemo(() => {
    const p: Record<string, string> = { q, from: f.from, to: f.to, status: '', ...tagFilterParams(tagF) };
    if (f.status === OPEN) p.open = '1';
    else if (f.status === OVERDUE) p.overdue = '1';
    else p.status = f.status;
    return p;
  }, [q, f.status, f.from, f.to, tagF]);
  const filterKey = JSON.stringify(filterParams);
  useEffect(() => { setPage(0); }, [filterKey, perPage, sort]);

  const res = useAdminGet(S, 'orders', {
    ...filterParams, page, per_page: perPage,
    sort: sort ? (SORT_BY_COL[sort.key] || sort.key) : '', dir: sort?.dir || '',
  }, [filterKey, page, perPage, sort, tick]);

  const d = res.data || {};
  // The server's own promise (ip_intel.TURNAROUND_DAYS) wins over the prop.
  const turn = num(d.turnaround_days) || turnaroundDays;
  const rawRows = rowsOf(d, 'orders');
  const per = Number(perPage) || 50;
  const { total, pages } = pageInfo(d, rawRows, per);
  const rows = useMemo(() => rawRows.map(r => ({
    ...r,
    customer: [r.name, r.email].filter(Boolean).join(' · '),
    lifecycle_tags: lifecycleText(r),
    due: dueAt(r, turn)?.toISOString() || '',
  })), [rawRows, turn]);
  const counts: Record<string, number> = useMemo(() => {
    const v = d.status_counts || d.counts;
    if (Array.isArray(v)) return Object.fromEntries(v.map((x: any) => [str(x.status), num(x.count ?? x.n)]));
    if (v && typeof v === 'object') return Object.fromEntries(Object.entries(v).map(([s, n]) => [s, num(n)]));
    return {};
  }, [d]);


  const chips: { label: string; value: string; clear: () => void }[] = [];
  if (f.q) chips.push({ label: 'Search', value: f.q, clear: () => set('q')('') });
  if (f.status) chips.push({
    label: 'Status',
    value: f.status === OPEN ? 'Open (paid, not delivered)' : f.status === OVERDUE ? 'Overdue' : STATUS_META[f.status]?.label || f.status,
    clear: () => set('status')(''),
  });
  if (f.from) chips.push({ label: 'From', value: f.from, clear: () => set('from')('') });
  if (f.to) chips.push({ label: 'To', value: f.to, clear: () => set('to')('') });
  if (tagFilterActive(tagF)) chips.push({
    label: 'Lifecycle / tags',
    value: describeTagFilter(tagF, k => tagOpts.tagByKey[k]?.label || k).join(' · '),
    clear: () => setTagF(EMPTY_TAG_FILTER),
  });

  const statusOptions = ['', OPEN, OVERDUE, ...STATUSES];
  const statusLabels: Record<string, string> = {
    '': 'Any status', [OPEN]: 'Open (paid, not delivered)', [OVERDUE]: 'Overdue',
    ...Object.fromEntries(STATUSES.map(s => [s, `${STATUS_META[s].label}${counts[s] !== undefined ? ` (${counts[s]})` : ''}`])),
  };

  return (
    <div className="flex flex-col gap-4">
      <Section title="Orders"
        right={
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-grayscale-1100">{total.toLocaleString()} order{total === 1 ? '' : 's'}</span>
            {chips.length > 0 && (
              <Btn small onClick={() => { setF(EMPTY); setTagF(EMPTY_TAG_FILTER); }}>
                Clear {chips.length} filter{chips.length === 1 ? '' : 's'}
              </Btn>
            )}
            <Btn small onClick={() => setTick(t => t + 1)} disabled={res.loading}>Refresh</Btn>
            <ExportButtons kind="orders" params={filterParams} onError={setDlErr} what="The orders below" />
          </div>
        }>
        <div className="flex items-center gap-2 flex-wrap">
          <TextInput value={f.q} onChange={set('q')} wide
            placeholder="Search ref, name, email, organisation, Razorpay id…" />
          <Select value={f.status} onChange={set('status')} options={statusOptions} labels={statusLabels} />
          <span className="text-xs text-grayscale-1100">created from</span>
          <TextInput type="date" value={f.from} onChange={set('from')} />
          <span className="text-xs text-grayscale-1100">to</span>
          <TextInput type="date" value={f.to} onChange={set('to')} />
        </div>
        <TagFilter value={tagF} onChange={v => setTagF(v)} />
        {chips.length > 0 && (
          <div className="flex gap-1.5 flex-wrap">
            {chips.map(c => <Chip key={c.label} label={c.label} value={c.value} onClear={c.clear} />)}
          </div>
        )}
        {dlErr && <Msg kind="error">Export failed: {dlErr}</Msg>}
        {res.error && <Msg kind="error">{res.error}</Msg>}
        {res.loading && !res.data ? <Loading /> : (
          <DataTable rows={rows} columns={COLS} labels={LABELS} maxHeight="40rem"
            serverSort={sort}
            onSortChange={(key, dir) => {
              if (!key || !dir) { setSort(null); return; }
              if (SORT_BY_COL[key]) setSort({ key, dir });
            }}
            empty={chips.length ? 'No orders match these filters.' : 'No orders yet.'}
            render={{
              ref: r => (
                <button type="button" onClick={() => setOpenId(str(r.id))} title="Open this order"
                  className="font-mono text-xs font-bold text-grayscale-1300 underline underline-offset-2">
                  {r.ref || str(r.id).slice(0, 8)}
                </button>
              ),
              status: r => (
                <span className="inline-flex items-center gap-1">
                  <StatusPill status={r.status} />
                  {isOverdue(r, turn) && (
                    <span className="text-[10px] font-bold uppercase text-red-600" title="Past the turnaround promise">overdue</span>
                  )}
                </span>
              ),
              customer: r => (
                <span className="flex flex-col">
                  <span className="font-medium truncate max-w-[14rem]">{r.name || '—'}</span>
                  {r.email && <span className="text-[11px] text-grayscale-1100 truncate max-w-[16rem]">{r.email}</span>}
                </span>
              ),
              lifecycle_tags: r => <span className="inline-block min-w-[12rem]"><UserTagCells row={r} /></span>,
              total_minor: r => num(r.total_minor) ? money(num(r.total_minor), r.currency || 'INR') : '—',
              technology_title: r => r.technology_title
                ? <span className="block max-w-[16rem] truncate" title={r.technology_title}>
                    {r.technology_title}{r.trl ? <span className="text-[11px] text-grayscale-1100"> · TRL {r.trl}</span> : null}
                  </span>
                : '—',
              due: r => {
                if (!r.due) return '—';
                const late = isOverdue(r, turn);
                return <span className={late ? 'text-red-600 font-medium' : ''}>{fmtDate(r.due)}</span>;
              },
            }}
            actions={r => <Btn small onClick={() => setOpenId(str(r.id))}>Open</Btn>} />
        )}
        {pages > 1 && (
          <Pager page={page} pages={pages} total={total} shown={rawRows.length} per={per}
            onPage={setPage} perPage={perPage} onPerPage={setPerPage} />
        )}
      </Section>

      {openId && (
        <OrderDrawer orderId={openId} turnaroundDays={turn}
          onClose={() => setOpenId(null)}
          onChanged={() => { setTick(t => t + 1); onChanged?.(); }} />
      )}
    </div>
  );
};

export default Orders;
